import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (to: string) => {
    setMobileOpen(false);
    navigate(to);
  };

  const handleAnchor = (hash: string) => {
    setMobileOpen(false);
    if (window.location.pathname === '/') {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/' + hash);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap font-label">
              Home
            </Link>
            <button onClick={() => handleAnchor('#categories')} className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap font-label cursor-pointer bg-transparent border-none p-0">
              Categories
            </button>
            <button onClick={() => handleAnchor('#guides')} className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap font-label cursor-pointer bg-transparent border-none p-0">
              Guides
            </button>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/" className="text-lg md:text-xl font-heading font-bold text-foreground-950 tracking-tight whitespace-nowrap">
              Hipobuy Spreadsheet
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => handleAnchor('#comparisons')} className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap font-label cursor-pointer bg-transparent border-none p-0">
              Comparisons
            </button>
            <button onClick={() => handleAnchor('#safety')} className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap font-label cursor-pointer bg-transparent border-none p-0">
              Safety
            </button>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Hipobuy Sheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[80vh] overflow-y-auto">
          {/* Main nav links */}
          <div className="px-4 py-3 flex flex-col gap-1 border-b border-background-200">
            <button onClick={() => handleNav('/')} className="text-left text-sm font-medium text-foreground-800 font-label py-2.5 hover:text-primary-600 transition-colors cursor-pointer">Home</button>
            <button onClick={() => handleAnchor('#categories')} className="text-left text-sm font-medium text-foreground-800 font-label py-2.5 hover:text-primary-600 transition-colors cursor-pointer">Categories</button>
            <button onClick={() => handleAnchor('#guides')} className="text-left text-sm font-medium text-foreground-800 font-label py-2.5 hover:text-primary-600 transition-colors cursor-pointer">Guides</button>
            <button onClick={() => handleAnchor('#comparisons')} className="text-left text-sm font-medium text-foreground-800 font-label py-2.5 hover:text-primary-600 transition-colors cursor-pointer">Comparisons</button>
            <button onClick={() => handleAnchor('#safety')} className="text-left text-sm font-medium text-foreground-800 font-label py-2.5 hover:text-primary-600 transition-colors cursor-pointer">Safety</button>
          </div>

          {/* CTA */}
          <div className="px-4 py-3 border-b border-background-200">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold font-label hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Hipobuy Sheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>

          {/* Category accordion */}
          <div className="px-4 py-3">
            <button
              onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
              className="flex items-center justify-between w-full text-left cursor-pointer"
            >
              <span className="text-sm font-semibold text-foreground-950 font-label">Shop Categories</span>
              <span className="w-5 h-5 flex items-center justify-center text-foreground-600">
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileCatsOpen ? 'rotate-180' : ''}`}></i>
              </span>
            </button>

            {mobileCatsOpen && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 bg-background-100 rounded-xl text-xs font-medium text-foreground-800 font-label hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center shrink-0 text-primary-500">
                      <i className="ri-shopping-bag-3-line text-sm"></i>
                    </span>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}